/**
 * Sending a customer their copy of a contract they have already signed.
 *
 * The Edge Function sends this letter by itself the moment a signature lands
 * (ADR 0122). This is the same letter, sent by hand, and it exists for two
 * kinds of customer: the ones who signed before any of that was built — Katie
 * Harding among them — and the ones whose copy did not go because the rep's
 * Google account was not connected that day.
 *
 * Every decision in it comes from packages/core — the words
 * (`signedCopyLetter`), what they bought (`signedCopyLines`) and who it goes to
 * (`signedCopyRecipient`) — and the Edge Function's transcription of each is
 * pinned to those, so the copy sent from a job screen and the copy sent
 * automatically are the same letter to the same person (CLAUDE.md rule 5).
 * What is left here is fetching and sending.
 *
 * It goes out of the mailbox of whoever pressed the button, because that is
 * whose Gmail `gmail-send` holds a token for, and it is signed with their name
 * for the same reason the payment letters are: a customer replying should reach
 * the person who wrote to them.
 */

import {
  COMPANY_NAME,
  signedCopyLetter,
  signedCopyLines,
  signedCopyRecipient,
  signedEstimateIds,
  toCents,
} from "@roof-hawk/core";

import { sendEmail } from "./gmail";
import {
  fetchCompanySettings,
  fetchContact,
  fetchProposalRecord,
} from "./queries";
import { supabase } from "./supabase";

/** The service address in one line, as the letter prints it. */
function addressOf(job: {
  address: string | null;
  city: string | null;
}): string | null {
  return [job.address, job.city].filter(Boolean).join(", ") || null;
}

export interface SignedCopySent {
  /** Where it went, so the screen can say who has it. */
  to: string;
  /**
   * Said alongside, when the letter went but the row could not be told.
   *
   * Not a failure — the customer has their copy — but Docs reads the row, and
   * without this it would go on saying "no copy sent" with nobody told why.
   */
  warning: string | null;
}

export const COPY_NOT_RECORDED =
  "Docs could not record that it went, so this row may still say no copy sent.";

/**
 * Send one signed copy, and record that it went.
 *
 * Everything it needs is read here rather than passed in: the letter has to
 * describe what was signed, not what the screen happens to be holding.
 */
export async function emailSignedCopy(input: {
  proposalId: string;
  job: {
    id: string;
    contact_id: string;
    address: string | null;
    city: string | null;
  };
  /** Whoever is signed in — the letter is signed with their name. */
  senderProfileId: string | null;
}): Promise<SignedCopySent> {
  const record = await fetchProposalRecord(input.proposalId);
  const signature = record.signature;
  if (!signature) {
    throw new Error(
      "Nobody has signed this document, so there is no copy to send.",
    );
  }

  const [contact, company] = await Promise.all([
    fetchContact(input.job.contact_id),
    fetchCompanySettings(),
  ]);

  const recipient = signedCopyRecipient(signature.signerEmail, contact);
  if (!recipient.ok) throw new Error(recipient.error);

  const me = input.senderProfileId
    ? ((
        await supabase
          .from("profiles")
          .select("full_name,phone")
          .eq("id", input.senderProfileId)
          .maybeSingle()
      ).data as { full_name: string | null; phone: string | null } | null)
    : null;

  const letter = signedCopyLetter({
    customerName: contact?.full_name ?? null,
    address: addressOf(input.job),
    signedDate: signature.signedDate,
    // Every option they bought, not only the first (ADR 0120).
    lines: signedCopyLines(
      record.document,
      signedEstimateIds(signature),
      signature.upgradeKeys,
    ),
    // `total` came off `numeric` in dollars; the letter prints cents.
    total: toCents(signature.total),
    link: `${window.location.origin}/p/${record.token}`,
    repName: me?.full_name ?? null,
    repPhone: me?.phone ?? null,
    // CLAUDE.md rule 11: the name is data, and the fallback is only for a
    // database that has not been filled in.
    companyName: (company?.name ?? "").trim() || COMPANY_NAME,
    companyPhone: company?.phone ?? null,
  });

  await sendEmail({
    to: recipient.to,
    subject: letter.subject,
    text: letter.text,
    jobId: input.job.id,
    contactId: input.job.contact_id,
    // It closes "Thank you," with the rep and the company already, and the
    // copy the server sends on its own is unsigned. One letter, one shape.
    sign: false,
  });

  // The same two columns the Edge Function writes, so Docs tells one story
  // about this document whichever path the letter took. A failure to record it
  // is not a failure to send it — the customer has the letter either way — so
  // it is said, not thrown.
  const { error } = await supabase
    .from("proposals")
    .update({
      signed_copy_sent_at: new Date().toISOString(),
      signed_copy_error: null,
    })
    .eq("id", input.proposalId);

  return { to: recipient.to, warning: error ? COPY_NOT_RECORDED : null };
}
